<script lang="ts">
  import { type Business } from '../../lib/types';

  let {
    businesses,
    selectedBusinessId,
    defaultBusinessId,
    onselect,
    oncreatenew,
  }: {
    businesses: Business[];
    selectedBusinessId: string | null;
    defaultBusinessId: string | null;
    onselect: (business: Business) => void;
    oncreatenew: () => void;
  } = $props();
</script>

<aside class="business-list-panel">
  <div class="panel-header">
    <h3>Businesses</h3>
    <button class="btn-add-business" onclick={oncreatenew} title="Add Business">
      <svg viewBox="0 0 20 20" fill="currentColor">
        <path d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" />
      </svg>
    </button>
  </div>

  {#if businesses.length === 0}
    <div class="empty-business-list">
      <p>No businesses yet</p>
      <button class="btn-create-first" onclick={oncreatenew}>
        Create Your First Business
      </button>
    </div>
  {:else}
    <div class="business-list">
      {#each businesses as business}
        <button
          class="business-list-item"
          class:selected={selectedBusinessId === business.id}
          class:inactive={business.isActive === false}
          onclick={() => onselect(business)}
        >
          <div class="business-item-avatar">
            {business.name.charAt(0).toUpperCase()}
          </div>
          <div class="business-item-info">
            <span class="business-item-name">
              {business.name}
              {#if defaultBusinessId === business.id}
                <span class="default-indicator" title="Default business for EQUOS app">&#10003;</span>
              {/if}
            </span>
            {#if business.isActive === false}
              <span class="business-item-status inactive">Inactive</span>
            {:else if business.industry}
              <span class="business-item-industry">{business.industry}</span>
            {/if}
          </div>
        </button>
      {/each}
    </div>
  {/if}
</aside>

<style>
  .business-list-panel {
    width: 260px;
    flex-shrink: 0;
    background: #f9fafb;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .panel-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem 1rem 0.75rem;
    border-bottom: 1px solid #e5e7eb;
    background: white;
  }

  .panel-header h3 {
    margin: 0;
    font-size: 0.95rem;
    font-weight: 600;
    color: #333;
  }

  .btn-add-business {
    width: 32px;
    height: 32px;
    border: none;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 6px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    transition: all 0.2s;
  }

  .btn-add-business:hover {
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  }

  .btn-add-business svg {
    width: 18px;
    height: 18px;
  }

  .empty-business-list {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2rem 1rem;
    text-align: center;
  }

  .empty-business-list p {
    margin: 0 0 1rem;
    color: #666;
    font-size: 0.9rem;
  }

  .btn-create-first {
    padding: 0.75rem 1rem;
    border: none;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border-radius: 6px;
    font-weight: 600;
    font-size: 0.85rem;
    cursor: pointer;
    transition: all 0.2s;
  }

  .btn-create-first:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  }

  .business-list {
    flex: 1;
    overflow-y: auto;
    padding: 0.5rem;
  }

  .business-list-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    width: 100%;
    padding: 0.75rem;
    border: none;
    background: transparent;
    border-radius: 6px;
    cursor: pointer;
    text-align: left;
    transition: all 0.15s;
    margin-bottom: 0.25rem;
  }

  .business-list-item:hover {
    background: #e5e7eb;
  }

  .business-list-item.selected {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
  }

  .business-item-avatar {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1rem;
    font-weight: 600;
    flex-shrink: 0;
  }

  .business-list-item.selected .business-item-avatar {
    background: rgba(255, 255, 255, 0.2);
  }

  .business-item-info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
  }

  .business-item-name {
    font-weight: 500;
    font-size: 0.9rem;
    color: #333;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: flex;
    align-items: center;
    gap: 0.375rem;
  }

  .default-indicator {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border-radius: 50%;
    font-size: 0.6rem;
    font-weight: 700;
    flex-shrink: 0;
  }

  .business-list-item.selected .default-indicator {
    background: rgba(255, 255, 255, 0.3);
  }

  .business-list-item.selected .business-item-name {
    color: white;
  }

  .business-item-industry {
    font-size: 0.75rem;
    color: #666;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .business-list-item.selected .business-item-industry {
    color: rgba(255, 255, 255, 0.8);
  }

  .business-list-item.inactive {
    opacity: 0.6;
  }

  .business-list-item.inactive .business-item-avatar {
    background: #9ca3af;
  }

  .business-item-status {
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .business-item-status.inactive {
    color: #dc2626;
  }

  .business-list-item.selected .business-item-status {
    color: rgba(255, 255, 255, 0.9);
  }

  @media (max-width: 768px) {
    .business-list-panel {
      width: 100%;
      max-height: 200px;
    }

    .business-list {
      flex-direction: row;
      display: flex;
      gap: 0.5rem;
      overflow-x: auto;
      overflow-y: hidden;
      padding: 0.5rem;
    }

    .business-list-item {
      flex-shrink: 0;
      width: auto;
      min-width: 150px;
      margin-bottom: 0;
    }
  }
</style>
